/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Plus, ChevronDown, ChevronUp, Pencil, Trash2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MatchTable from "@/pages/matches/MatchTable";
import MatchForm from "@/pages/matches/MatchForm";
import MatchView from "@/pages/matches/MatchView";
import {
  useMatchesAdmin,
  useCreateMatch,
  useUpdateMatch,
  useDeleteMatch,
  useUpdateMatchStatus,
} from "@/hooks/api/useMatches";
import {
  useGameweeks,
  useCreateGameweek,
  useUpdateGameweek,
  useDeleteGameweek,
} from "@/hooks/api/useGameweeks";
import type {
  Match,
  CreateMatchRequest,
  UpdateMatchRequest,
  MatchFilters,
  MatchStatus,
  Gameweek,
} from "@/types/api.types";

const GW_STATUS_COLORS: Record<string, string> = {
  UPCOMING: "bg-gray-100 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-yellow-100 text-yellow-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

export default function MatchManagement() {
  const [filters, setFilters] = useState<MatchFilters>({ page: 1, limit: 10, sortBy: "status" });
  const [isOpen, setIsOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState<Partial<Match> | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Matchweeks panel state
  const [showMatchweeks, setShowMatchweeks] = useState(false);
  const [gwCreateOpen, setGwCreateOpen] = useState(false);
  const [gwName, setGwName] = useState("");
  const [gwDeadline, setGwDeadline] = useState("");
  const [editingGw, setEditingGw] = useState<Gameweek | null>(null);
  const [editGwName, setEditGwName] = useState("");
  const [editGwDeadline, setEditGwDeadline] = useState("");

  const { data: gameweeksData } = useGameweeks();
  const gameweeks: Gameweek[] = Array.isArray(gameweeksData) ? gameweeksData : [];
  const { mutate: createGameweek, isPending: creatingGw } = useCreateGameweek();
  const { mutate: updateGameweek, isPending: updatingGw } = useUpdateGameweek();
  const { mutate: deleteGameweek } = useDeleteGameweek();

  const { data, isLoading, error } = useMatchesAdmin(filters);
  const { mutate: createMatch, isPending: isCreating } = useCreateMatch();
  const { mutate: updateMatch, isPending: isUpdating } = useUpdateMatch();
  const { mutate: deleteMatch, isPending: isDeleting } = useDeleteMatch();
  const { mutate: updateStatus } = useUpdateMatchStatus();

  const toLocalDatetime = (iso: string) => {
    const d = new Date(iso);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  };

  const handleCreateGw = () => {
    if (!gwName.trim()) { toast.error("Name is required"); return; }
    if (!gwDeadline) { toast.error("Deadline is required"); return; }
    const roundNumber = gameweeks.filter((g) => g.phase === "GROUP_STAGE").length + 1;
    createGameweek(
      { name: gwName.trim(), phase: "GROUP_STAGE", roundNumber, deadline: new Date(gwDeadline).toISOString() },
      {
        onSuccess: () => { toast.success(`Matchweek "${gwName}" created`); setGwCreateOpen(false); setGwName(""); setGwDeadline(""); },
        onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create matchweek"),
      }
    );
  };

  const handleStartEditGw = (gw: Gameweek) => {
    setEditingGw(gw);
    setEditGwName(gw.name);
    setEditGwDeadline(toLocalDatetime(gw.deadline));
  };

  const handleUpdateGw = () => {
    if (!editingGw) return;
    if (!editGwName.trim()) { toast.error("Name is required"); return; }
    updateGameweek(
      { id: editingGw.id, data: { name: editGwName.trim(), deadline: new Date(editGwDeadline).toISOString() } },
      {
        onSuccess: () => { toast.success("Matchweek updated"); setEditingGw(null); },
        onError: (e: any) => toast.error(e.response?.data?.message || "Failed to update matchweek"),
      }
    );
  };

  const handleDeleteGw = (gw: Gameweek) => {
    if (!confirm(`Delete matchweek "${gw.name}"? This will unassign all its matches.`)) return;
    deleteGameweek(gw.id, {
      onSuccess: () => toast.success(`Matchweek "${gw.name}" deleted`),
      onError: (e: any) => toast.error(e.response?.data?.message || "Failed to delete matchweek"),
    });
  };

  const handleAddMatch = () => {
    setIsEditing(false);
    setCurrentMatch(null);
    setIsOpen(true);
  };

  const handleEditMatch = (match: Match) => {
    setIsEditing(true);
    setCurrentMatch(match);
    setIsOpen(true);
  };

  const handleViewMatch = (match: Match) => {
    setCurrentMatch(match);
    setViewOpen(true);
  };

  const handleDeleteMatch = (match: Match) => {
    const label = `${match.homeNation?.name ?? "?"} vs ${match.awayNation?.name ?? "?"}`;
    const extra = match.status === "LIVE" || match.status === "COMPLETED"
      ? `\n\nThis match is ${match.status}. Logged events will be deleted and points may need to be recalculated.`
      : "";
    if (!confirm(`Delete match "${label}"?${extra}`)) return;
    deleteMatch(match.id, {
      onSuccess: () => toast.success(`Match "${label}" deleted`),
      onError: (error: any) =>
        toast.error(error.response?.data?.message || "Failed to delete match"),
    });
  };

  const handleUpdateStatus = (match: Match, status: MatchStatus) => {
    const statusLabels: Record<MatchStatus, string> = {
      SCHEDULED: "scheduled",
      LIVE: "live",
      COMPLETED: "completed",
      POSTPONED: "postponed",
      CANCELLED: "cancelled",
    };

    const confirmMessages: Record<MatchStatus, string> = {
      LIVE: `Start "${match.homeNation.name} vs ${match.awayNation.name}" and set it LIVE?\n\nMake sure the match has actually kicked off.`,
      COMPLETED: `Mark "${match.homeNation.name} ${match.homeScore ?? 0}–${match.awayScore ?? 0} ${match.awayNation.name}" as COMPLETED?\n\nThis will finalise the result and cannot be undone.`,
      POSTPONED: `Postpone "${match.homeNation.name} vs ${match.awayNation.name}"?\n\nThe match will be moved to POSTPONED status.`,
      CANCELLED: `Cancel "${match.homeNation.name} vs ${match.awayNation.name}"?\n\nThis cannot be undone.`,
      SCHEDULED: `Move "${match.homeNation.name} vs ${match.awayNation.name}" back to SCHEDULED?`,
    };

    if (!confirm(confirmMessages[status])) return;

    // Clear timer data when setting match to LIVE to ensure fresh start
    if (status === "LIVE") {
      localStorage.removeItem(`match_timer_${match.id}`);
    }

    updateStatus(
      { id: match.id, status },
      {
        onSuccess: () =>
          toast.success(`Match marked as ${statusLabels[status]}`),
        onError: (error: any) =>
          toast.error(
            error.response?.data?.message || "Failed to update status",
          ),
      },
    );
  };

  const handleSaveMatch = (
    formData: CreateMatchRequest | UpdateMatchRequest,
  ) => {
    const normalized = {
      ...formData,
      kickoffTime: formData.kickoffTime
        ? new Date(formData.kickoffTime).toISOString()
        : undefined,
    };
    if (isEditing && currentMatch?.id) {
      updateMatch(
        { id: currentMatch.id, data: normalized as UpdateMatchRequest },
        {
          onSuccess: () => {
            toast.success("Match updated successfully");
            setIsOpen(false);
            setCurrentMatch(null);
          },
          onError: (error: any) =>
            toast.error(
              error.response?.data?.message || "Failed to update match",
            ),
        },
      );
    } else {
      createMatch(normalized as CreateMatchRequest, {
        onSuccess: () => {
          toast.success("Match created successfully");
          setIsOpen(false);
        },
        onError: (error: any) =>
          toast.error(
            error.response?.data?.message || "Failed to create match",
          ),
      });
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load matches</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Match Management</h1>
          <p className="text-gray-500 mt-1">
            Manage World Cup 2026 fixtures and results
          </p>
        </div>
        <Button
          onClick={handleAddMatch}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isCreating}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Match
        </Button>
      </div>

      {/* Matchweeks Panel */}
      <div className="mb-6 bg-white rounded-lg shadow border border-gray-200">
        <button
          type="button"
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setShowMatchweeks((v) => !v)}
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="font-semibold text-gray-800">Matchweeks</span>
            <Badge className="bg-blue-100 text-blue-700 font-medium">{gameweeks.length}</Badge>
          </div>
          {showMatchweeks ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </button>

        {showMatchweeks && (
          <div className="border-t border-gray-100 px-5 py-4 space-y-3">
            {/* Create form toggle */}
            {!gwCreateOpen ? (
              <Button size="sm" variant="outline" onClick={() => setGwCreateOpen(true)} className="gap-1">
                <Plus className="h-3 w-3" /> New Matchweek
              </Button>
            ) : (
              <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-md border">
                <div>
                  <Label className="text-xs mb-1 block">Name <span className="text-red-500">*</span></Label>
                  <Input value={gwName} onChange={(e) => setGwName(e.target.value)} placeholder="Matchweek 1" className="h-8 text-sm w-48" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Pick Deadline <span className="text-red-500">*</span></Label>
                  <Input type="datetime-local" value={gwDeadline} onChange={(e) => setGwDeadline(e.target.value)} className="h-8 text-sm" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateGw} disabled={creatingGw}>{creatingGw ? "Creating…" : "Create"}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setGwCreateOpen(false); setGwName(""); setGwDeadline(""); }}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Gameweek list */}
            {gameweeks.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">No matchweeks yet. Create one above.</p>
            ) : (
              <div className="space-y-2">
                {gameweeks.map((gw) => (
                  <div key={gw.id} className="border rounded-md p-3 bg-gray-50">
                    {editingGw?.id === gw.id ? (
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <Label className="text-xs mb-1 block">Name</Label>
                          <Input value={editGwName} onChange={(e) => setEditGwName(e.target.value)} className="h-8 text-sm w-44" />
                        </div>
                        <div>
                          <Label className="text-xs mb-1 block">Deadline</Label>
                          <Input type="datetime-local" value={editGwDeadline} onChange={(e) => setEditGwDeadline(e.target.value)} className="h-8 text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleUpdateGw} disabled={updatingGw}>{updatingGw ? "Saving…" : "Save"}</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingGw(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-sm text-gray-800">{gw.name}</span>
                          <Badge className={`text-xs ${GW_STATUS_COLORS[gw.status] ?? "bg-gray-100 text-gray-600"}`}>{gw.status}</Badge>
                          <span className="text-xs text-gray-400">
                            Deadline: {new Date(gw.deadline).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {gw._count?.gameweekTeams != null && (
                            <span className="text-xs text-gray-400">{gw._count.gameweekTeams} teams</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleStartEditGw(gw)} title="Edit matchweek">
                            <Pencil className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteGw(gw)} title="Delete matchweek">
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <MatchTable
        matches={data?.matches || []}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={(f) => setFilters({ ...f, page: 1 })}
        onEdit={handleEditMatch}
        onView={handleViewMatch}
        onDelete={handleDeleteMatch}
        onUpdateStatus={handleUpdateStatus}
        isDeleting={isDeleting}
        pagination={data?.pagination}
        onPageChange={(page: number) => setFilters((prev) => ({ ...prev, page }))}
        onPageSizeChange={(limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))}
        gameweeks={gameweeks}
      />

      <MatchForm
        open={isOpen}
        onOpenChange={setIsOpen}
        match={currentMatch}
        isEditing={isEditing}
        onSave={handleSaveMatch}
        isSaving={isCreating || isUpdating}
      />

      <MatchView
        open={viewOpen}
        onOpenChange={setViewOpen}
        match={currentMatch as Match}
      />
    </Layout>
  );
}
